import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Select } from './ui/Input';
import { IS_ADMIN_SURFACE } from '../utils/surface';
import { apiList, apiUpdate } from '../api/apiClient';

interface Props {
  // What we're reassigning. 'property' lets you pick a new customer + dealer;
  // 'customer' lets you pick a new dealer.
  entityType: 'property' | 'customer';
  entityId: string | number;
  // Current parents (for showing "Current: X" + computing diffs)
  currentCustomerId?: string | number | null;
  currentDealerId?: string | number | null;
  // Fired after a successful PUT so the parent page can refresh.
  onSaved?: () => void;
}

// Admin-only "Reassign" card. Lets a super-admin move a property to a
// different customer/dealer, or move a customer to a different dealer, with
// one PUT. Hidden on every non-admin surface.
export const AdminReassignCard: React.FC<Props> = ({
  entityType, entityId, currentCustomerId, currentDealerId, onSaved,
}) => {
  const [dealers,   setDealers]   = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [pickedCustomer, setPickedCustomer] = useState<string>(String(currentCustomerId ?? ''));
  const [pickedDealer,   setPickedDealer]   = useState<string>(String(currentDealerId   ?? ''));
  const [busy, setBusy] = useState(false);
  const [msg,  setMsg]  = useState<string | null>(null);

  useEffect(() => {
    if (!IS_ADMIN_SURFACE) return;
    Promise.all([
      apiList('dealers').catch(() => []),
      apiList('customers').catch(() => []),
    ]).then(([d, c]) => {
      setDealers((d as any[]) || []); setCustomers((c as any[]) || []);
    });
  }, []);

  useEffect(() => { setPickedCustomer(String(currentCustomerId ?? '')); }, [currentCustomerId]);
  useEffect(() => { setPickedDealer(String(currentDealerId ?? '')); },   [currentDealerId]);

  // Limit customer dropdown to those under the picked dealer (or all when
  // no dealer is picked yet).
  const customersForDealer = useMemo(() => {
    if (!pickedDealer) return customers;
    return customers.filter(c => String(c.dealer_id || c.integrator_id) === String(pickedDealer));
  }, [customers, pickedDealer]);

  // When the user picks a customer, snap the dealer to that customer's dealer.
  const handleCustomerChange = (v: string) => {
    setPickedCustomer(v);
    const c = customers.find(x => String(x.id) === String(v) || String(x.uuid) === String(v));
    if (c) setPickedDealer(String(c.dealer_id || c.integrator_id || ''));
  };

  const dirty = entityType === 'property'
    ? (String(currentCustomerId ?? '') !== pickedCustomer)
    : (String(currentDealerId ?? '')   !== pickedDealer);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      if (entityType === 'property') {
        const c = customers.find(x => String(x.id) === pickedCustomer || String(x.uuid) === pickedCustomer);
        const dealerId = c?.dealer_id || c?.integrator_id || pickedDealer || null;
        await apiUpdate('properties', entityId, { customer_id: pickedCustomer || null, dealer_id: dealerId });
      } else {
        await apiUpdate('customers', entityId, { dealer_id: pickedDealer || null });
      }
      setMsg('Saved. Reloading…');
      onSaved?.();
    } catch (e: any) {
      setMsg(`Save failed: ${e?.message || e}`);
    } finally { setBusy(false); }
  };

  if (!IS_ADMIN_SURFACE) return null;

  const nameOf = (arr: any[], id: any) =>
    arr.find(x => String(x.id) === String(id) || String(x.uuid) === String(id))?.name || '—';

  return (
    <Card>
      <CardHeader
        title="Reassign (Admin)"
        subtitle={`Move this ${entityType} to a different ${entityType === 'property' ? 'customer + dealer' : 'dealer'} in one click.`}
      />
      <div className="space-y-3 mt-3">
        {entityType === 'property' && (
          <Select label="Customer" value={pickedCustomer} onChange={(e: any) => handleCustomerChange(e.target.value)}>
            <option value="">— Unassigned —</option>
            {customersForDealer.map(c => (
              <option key={c.uuid || c.id} value={c.uuid || c.id}>
                {c.name || c.email || `#${c.id}`}{c.dealer_id ? ` (dealer ${nameOf(dealers, c.dealer_id)})` : ''}
              </option>
            ))}
          </Select>
        )}
        <Select label="Dealer" value={pickedDealer} onChange={(e: any) => setPickedDealer(e.target.value)}>
          <option value="">— Unassigned —</option>
          {dealers.map(d => (
            <option key={d.uuid || d.id} value={d.uuid || d.id}>{d.name || `#${d.id}`}</option>
          ))}
        </Select>

        <div className="text-xs text-gray-500 -mt-2">
          Current: {entityType === 'property'
            ? <>customer <Badge color="gray">{nameOf(customers, currentCustomerId)}</Badge> · dealer <Badge color="gray">{nameOf(dealers, currentDealerId)}</Badge></>
            : <>dealer <Badge color="gray">{nameOf(dealers, currentDealerId)}</Badge></>}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">{msg || (dirty ? 'Unsaved changes' : 'Up to date')}</span>
          <Button onClick={save} disabled={!dirty || busy}>{busy ? 'Saving…' : 'Save Reassignment'}</Button>
        </div>
      </div>
    </Card>
  );
};
